import { Eye, Edit, ChevronUp, ChevronDown, DollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import {
  getStatusBadgeColor,
  getStatusDisplayName,
  formatPaymentMethod,
  type PaymentStatus,
} from "../../utils/payments";

interface Payment {
  id: string;
  created_at: string;
  student_id: string;
  run_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  status: string;
  notes: string | null;
  stripe_payment_intent_id: string | null;
  stripe_session_id: string | null;
  stripe_customer_id: string | null;
  currency: string | null;
}

interface PaymentWithDetails extends Payment {
  student?: {
    id: string;
    codername: string;
    parentname: string;
    email: string;
  };
  run?: {
    id: string;
    name: string;
    price: number;
    series: {
      id: string;
      title: string;
      code: string;
    };
  };
}

interface PaymentsTableProps {
  payments: PaymentWithDetails[];
  isLoading?: boolean;
  isAdmin?: boolean;
  onUpdateStatus?: (id: string, status: string) => void;
  showActions?: boolean;
  onSort?: (field: SortField) => void;
  sortField?: SortField;
  sortDirection?: "asc" | "desc";
}

type SortField = "payment_id" | "for" | "amount" | "payment_date" | "status";

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatPaymentId = (payment: PaymentWithDetails): string => {
  // Prioritize Stripe Payment Intent ID for better tracking
  if (payment.stripe_payment_intent_id) {
    return payment.stripe_payment_intent_id;
  }

  // Fallback to Stripe Session ID if available
  if (payment.stripe_session_id) {
    return payment.stripe_session_id.substring(0, 16) + "...";
  }

  // Final fallback to database UUID (first 8 characters)
  return payment.id.substring(0, 8).toUpperCase();
};

const StatusBadge = ({ status }: { status: PaymentStatus }) => {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm  capitalize ${getStatusBadgeColor(
        status
      )}`}
    >
      {getStatusDisplayName(status)}
    </span>
  );
};

export default function PaymentsTable({
  payments,
  isLoading = false,
  isAdmin = false,
  onUpdateStatus,
  showActions = true,
  onSort,
  sortField,
  sortDirection,
}: PaymentsTableProps) {
  const SortIcon = ({ field }: { field: SortField }) => {
    const isCurrentField = sortField === field;
    return onSort ? (
      <span
        className={`inline-block ml-1 transition-opacity ${
          isCurrentField ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
      >
        {sortDirection === "asc" && isCurrentField ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </span>
    ) : null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-neutral-200">
        <thead className="bg-neutral-50">
          <tr>
            <th
              scope="col"
              className={`px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider ${
                onSort ? "cursor-pointer hover:text-neutral-700 group" : ""
              }`}
              onClick={() => onSort?.("payment_id")}
            >
              <span className="inline-flex items-center">
                Payment ID
                <SortIcon field="payment_id" />
              </span>
            </th>
            <th
              scope="col"
              className={`px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider ${
                onSort ? "cursor-pointer hover:text-neutral-700 group" : ""
              }`}
              onClick={() => onSort?.("for")}
            >
              <span className="inline-flex items-center">
                For
                <SortIcon field="for" />
              </span>
            </th>
            <th
              scope="col"
              className={`px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider ${
                onSort ? "cursor-pointer hover:text-neutral-700 group" : ""
              }`}
              onClick={() => onSort?.("amount")}
            >
              <span className="inline-flex items-center">
                Amount
                <SortIcon field="amount" />
              </span>
            </th>
            <th
              scope="col"
              className={`px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider ${
                onSort ? "cursor-pointer hover:text-neutral-700 group" : ""
              }`}
              onClick={() => onSort?.("status")}
            >
              <span className="inline-flex items-center">
                Status
                <SortIcon field="status" />
              </span>
            </th>
            <th
              scope="col"
              className={`px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider ${
                onSort ? "cursor-pointer hover:text-neutral-700 group" : ""
              }`}
              onClick={() => onSort?.("payment_date")}
            >
              <span className="inline-flex items-center">
                Payment Date
                <SortIcon field="payment_date" />
              </span>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider"
            >
              Method
            </th>
            {showActions && (
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-neutral-200">
          {payments.length === 0 ? (
            <tr>
              <td
                colSpan={showActions ? 7 : 6}
                className="px-6 py-12 text-center text-gray-500"
              >
                <DollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg ">No payments found</p>
                <p className="text-sm">
                  Try adjusting your search or filter criteria.
                </p>
              </td>
            </tr>
          ) : (
            payments.map((payment) => {
              const status = payment.status as PaymentStatus;
              return (
                <tr key={payment.id} className="hover:bg-neutral-50">
                  {/* Payment ID Column */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium">
                          <Link
                            to={`/payments/${payment.id}`}
                            className="text-link font-medium hover:text-hover"
                          >
                            {formatPaymentId(payment)}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* For Column - Student, Group, and Series */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">
                        {payment.student ? (
                          <Link
                            to={`/students/${payment.student.id}`}
                            className="text-link hover:text-hover"
                          >
                            {payment.student.codername}
                          </Link>
                        ) : (
                          "Unknown Student"
                        )}
                      </div>
                      <div className="text-gray-500">
                        {payment.run ? (
                          <>
                            <Link
                              to={`/runs/${payment.run.id}`}
                              className="text-link hover:text-hover"
                            >
                              Group {payment.run.name}
                            </Link>
                            {payment.run.series && (
                              <>
                                {" "}
                                of{" "}
                                <Link
                                  to={`/series/${payment.run.series.id}`}
                                  className="text-link hover:text-hover"
                                >
                                  {payment.run.series.code}
                                </Link>
                              </>
                            )}
                          </>
                        ) : (
                          "No Group"
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm  ${getStatusBadgeColor(
                        status
                      )}`}
                    >
                      {formatCurrency(payment.amount)}
                      {payment.run?.price && (
                        <span className="ml-1">
                          {" / "}
                          {formatCurrency(payment.run.price)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isAdmin && onUpdateStatus ? (
                      <select
                        value={status}
                        onChange={(e) =>
                          onUpdateStatus(payment.id, e.target.value)
                        }
                        className="text-sm px-2 py-1 rounded border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="not_received">Not Received</option>
                        <option value="pending">Pending</option>
                        <option value="partial">Partial Payment</option>
                        <option value="completed">Paid</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    ) : (
                      <StatusBadge status={status} />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {status === "not_received"
                        ? "None"
                        : formatDate(payment.payment_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatPaymentMethod(
                        payment.payment_method || "",
                        status
                      )}
                    </div>
                  </td>
                  {showActions && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm ">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/payments/${payment.id}`}
                          className="icon-view"
                          title="View details"
                        >
                          <Eye className="h-5 w-5" />
                        </Link>
                        {isAdmin && (
                          <>
                            <Link
                              to={`/payments/${payment.id}/edit`}
                              className="icon-edit"
                              title="Edit payment"
                            >
                              <Edit className="h-5 w-5" />
                            </Link>
                            {/* <button
                              onClick={() => onDeletePayment?.(payment.id)}
                              className="icon-delete"
                              title="Delete payment"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button> */}
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
        {payments.length > 0 && (
          <tfoot className="bg-neutral-50">
            <tr>
              <td className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                Total
              </td>
              <td className="px-6 py-3 text-left text-sm text-neutral-500">
                {payments.length} payment{payments.length !== 1 ? "s" : ""}
              </td>
              <td className="px-8 py-3 text-right text-sm font-medium">
                {formatCurrency(
                  payments.reduce((sum, payment) => sum + payment.amount, 0)
                )}
              </td>
              <td className="px-6 py-3"></td>
              <td className="px-6 py-3"></td>
              <td className="px-6 py-3"></td>
              {showActions && <td className="px-6 py-3"></td>}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
